import type { FleetClaimProgress, NodeClaimRejection, NodeKeyHolder } from './types';

// Same convention as dataProvider.ts: relative in production, overridable in development.
const API_URL = import.meta.env.VITE_API_URL || '';
const BASE = `${API_URL}/api/admin/node-claims`;

const asJson = async (response: Response) => {
    if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || body?.detail || `Request failed (${response.status})`);
    }
    return response.json();
};

/**
 * Two calls here rather than through the shared dataProvider, for reasons that are
 * contract, not preference:
 *
 *  - key-holder is a sub-route with a query string, which dataProvider.getOne cannot express.
 *  - revoke must send a body. NodeClaimAuthorizationsController.Revoke binds
 *    [FromBody] RevokeNodeClaimRequest, and dataProvider.delete sends DELETE with no body,
 *    which the model binder rejects with a 400 before the handler ever runs.
 */

export const fetchKeyHolder = async (nodeId: string): Promise<NodeKeyHolder> =>
    asJson(
        await fetch(`${BASE}/key-holder?nodeId=${encodeURIComponent(nodeId)}`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        }),
    );

export const fetchFleetProgress = async (): Promise<FleetClaimProgress> =>
    asJson(
        await fetch(`${BASE}/progress`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        }),
    );

export const revokeAuthorization = async (id: number, reason: string): Promise<void> => {
    const response = await fetch(`${BASE}/${id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || body?.detail || `Revoke failed (${response.status})`);
    }
};

export const fetchRejections = async (outstandingOnly = true): Promise<NodeClaimRejection[]> =>
    asJson(
        await fetch(`${BASE}/rejections?outstandingOnly=${outstandingOnly}`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        }),
    );

/**
 * Acknowledging does not fix anything — the node is still being refused. It only clears the
 * alert from this view, and the server brings it back if the rejection happens again.
 */
export const acknowledgeRejection = async (id: number): Promise<void> => {
    const response = await fetch(`${BASE}/rejections/${id}/acknowledge`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
    });

    if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || body?.detail || `Acknowledge failed (${response.status})`);
    }
};
